import baseUrl from 'Base_Url/baseUrl';
import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.userId = null;
    this.token = null;
    this.isConnected = false;
    this.notificationCallbacks = [];
    this.syncCallbacks = [];
    this.reconnectionAttempts = 0;
    this.maxReconnectionAttempts = 10;
    this.pingInterval = null;
    this.connectionCheckInterval = null;

    this.baseUrl = this.extractSocketUrl(baseUrl);

    if (!this.baseUrl) {
      this.baseUrl = process.env.REACT_APP_BACKEND_URL;
    }

    this.setupPageVisibilityHandler();
  }

  extractSocketUrl(url) {
    if (!url) return null;

    let cleanUrl = url
      .replace(/\/api(\/)?$/, '')
      .replace(/\/v[0-9]+(\/)?$/, '')
      .replace(/\/$/, '');

    if (!cleanUrl.startsWith('http')) {
      cleanUrl = window.location.protocol + '//' + cleanUrl;
    }

    return cleanUrl;
  }

  setupPageVisibilityHandler() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          if (!this.isConnected || !this.socket?.connected) {
            setTimeout(() => {
              if (this.userId && this.token) {
                this.connect();
              }
            }, 1000);
          } else {
          }
        }
      });
    }
  }

  connect() {
    try {
      if (this.socket) {
        this.disconnect();
      }

      const userData = localStorage.getItem('user');
      const token =
        localStorage.getItem('token') || localStorage.getItem('jwtToken');

      if (!userData || !token) {
        return;
      }

      let userId;
      try {
        const user = JSON.parse(userData);
        userId = user.id || user._id || user.userId;

        if (!userId) {
          return;
        }
      } catch (error) {
        return;
      }

      this.userId = userId;
      this.token = token;

      if (!this.baseUrl) {
        return;
      }

      console.log('🔌 Connecting to socket:', {
        url: this.baseUrl,
        userId: userId,
        tokenPreview: token.substring(0, 20) + '...',
      });

      // Improved connection options
      this.socket = io(this.baseUrl, {
        auth: {
          token: token,
          userId: userId,
        },
        query: {
          userId: userId,
          clientType: 'web',
          version: '1.0',
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectionAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 30000,
        forceNew: true,
        multiplex: false,
        pingTimeout: 60000,
        pingInterval: 25000,
        upgradeTimeout: 10000,
        randomizationFactor: 0.5,
        autoConnect: true,
        withCredentials: true,
        extraHeaders: {
          'X-Client-Type': 'web-notification-panel',
        },
      });

      this.setupEventListeners();
      this.setupConnectionHealthCheck();
    } catch (error) {
      // Attempt reconnection after delay
      setTimeout(() => {
        if (this.userId && this.token) {
          this.connect();
        }
      }, 5000);
    }
  }

  setupEventListeners() {
    if (!this.socket) return;

    this.socket.onAny((eventName, ...args) => {
      if (eventName !== 'pong' && eventName !== 'ping') {
      }
    });

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectionAttempts = 0;

      this.syncCallbacks.forEach((callback) => {
        try {
          callback({
            type: 'connected',
            socketId: this.socket.id,
            timestamp: new Date(),
          });
        } catch (error) {}
      });
    });

    this.socket.on('connected', (data) => {
      if (data.userId) {
        this.userId = data.userId;
      }
    });

    this.socket.on('connect_error', (error) => {
      this.isConnected = false;

      this.syncCallbacks.forEach((callback) => {
        try {
          callback({
            type: 'connect_error',
            error: error.message,
            timestamp: new Date(),
          });
        } catch (error) {}
      });
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;

      this.syncCallbacks.forEach((callback) => {
        try {
          callback({ type: 'disconnected', reason, timestamp: new Date() });
        } catch (error) {}
      });

      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    });

    this.socket.on('reconnecting', (attemptNumber) => {
      console.log(
        `🔄 Reconnecting attempt ${attemptNumber}/${this.maxReconnectionAttempts}`,
      );
      this.reconnectionAttempts = attemptNumber;
    });

    this.socket.on('reconnect_failed', () => {
      this.isConnected = false;
    });

    this.socket.on('new_notification', (notificationData) => {
      if (notificationData.notification) {
        this.handleNotification(notificationData.notification);
      } else {
        this.handleNotification(notificationData);
      }
    });

    this.socket.on('notification', (notificationData) => {
      this.handleNotification(notificationData);
    });

    this.socket.on('file_status_update', (notificationData) => {
      if (notificationData.notification) {
        this.handleNotification(notificationData.notification);
      } else {
        const enhancedData = {
          ...notificationData,
          type: notificationData.type || 'file_status_update',
        };
        this.handleNotification(enhancedData);
      }
    });

    const fileEventTypes = [
      'file_upload',
      'file_approved',
      'file_rejected',
      'file_edit',
      'file_processed',
    ];
    fileEventTypes.forEach((eventType) => {
      this.socket.on(eventType, (notificationData) => {
        const notification = {
          ...notificationData,
          type: eventType,
          _id:
            notificationData._id ||
            notificationData.notification?._id ||
            `temp-${Date.now()}`,
        };

        this.handleNotification(notification);
      });
    });

    // System alerts
    this.socket.on('system_alert', (notificationData) => {
      const notification = {
        ...notificationData,
        type: 'system_alert',
        _id:
          notificationData._id ||
          notificationData.notification?._id ||
          `temp-${Date.now()}`,
      };

      this.handleNotification(notification);
    });

    // Notification status updates
    this.socket.on('notification_update', (data) => {
      this.syncCallbacks.forEach((callback) => {
        try {
          callback({
            type: 'notification_update',
            notificationId: data.notificationId,
            read: data.read,
            readAt: data.readAt,
            timestamp: new Date(),
          });
        } catch (error) {}
      });
    });

    this.socket.on('all_notifications_read', (data) => {
      this.syncCallbacks.forEach((callback) => {
        try {
          callback({
            type: 'all_notifications_read',
            count: data.count,
            timestamp: new Date(),
          });
        } catch (error) {}
      });
    });

    // Unread count updates
    this.socket.on('unread_count', (data) => {
      this.syncCallbacks.forEach((callback) => {
        try {
          callback({
            type: 'unread_count_update',
            count: data.count,
            timestamp: new Date(),
          });
        } catch (error) {}
      });
    });

    this.socket.on('unread_count_update', (data) => {
      this.syncCallbacks.forEach((callback) => {
        try {
          callback({
            type: 'unread_count_update',
            count: data.count,
            timestamp: new Date(),
          });
        } catch (error) {}
      });
    });

    // General sync events
    this.socket.on('notifications_sync', (data) => {
      this.syncCallbacks.forEach((callback) => {
        try {
          callback({ type: 'sync', data, timestamp: new Date() });
        } catch (error) {}
      });
    });

    // Ping/pong for connection health
    this.socket.on('ping', () => {
      if (this.socket) {
        this.socket.emit('pong');
      }
    });

    this.socket.on('pong', (data) => {});

    // Error handling
    this.socket.on('error', (error) => {});

    // Custom events
    this.socket.on('custom_event', (data) => {});

    // Test event for debugging
    this.socket.on('test_notification', (data) => {
      this.handleNotification(data);
    });
  }

  handleNotification(notificationData) {
    // Extract notification from wrapper if needed
    let notification = notificationData;

    if (notificationData.notification) {
      notification = notificationData.notification;
    }

    // Validate and normalize the notification
    if (!notification) {
      return;
    }

    // Ensure notification has required fields
    if (!notification._id) {
      notification._id = `temp-${Date.now()}`;
      console.warn('⚠️ Notification missing _id, using temporary ID');
    }

    if (!notification.type) {
      notification.type = notificationData.type || 'system_alert';
      console.warn(
        `⚠️ Notification missing type, defaulting to: ${notification.type}`,
      );
    }

    // Ensure read status
    if (notification.read === undefined) {
      notification.read = false;
    }

    // Ensure createdAt
    if (!notification.createdAt) {
      notification.createdAt = new Date().toISOString();
    }

    console.log('✅ Processed notification:', {
      id: notification._id,
      type: notification.type,
      title: notification.title,
      read: notification.read,
    });

    // Call notification callbacks
    this.notificationCallbacks.forEach((callback) => {
      try {
        callback(notificationData);
      } catch (error) {}
    });

    // Also call sync callbacks for notification events
    this.syncCallbacks.forEach((callback) => {
      try {
        callback({
          type: 'new_notification_received',
          notification: notificationData,
          timestamp: new Date(),
        });
      } catch (error) {}
    });

    // Show browser notification
    this.showBrowserNotification(notification);
  }

  showBrowserNotification(notification) {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          this.createBrowserNotification(notification);
        }
      });
    } else if (Notification.permission === 'granted') {
      this.createBrowserNotification(notification);
    }
  }

  createBrowserNotification(notification) {
    const options = {
      body: notification.message || notification.title || 'New notification',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notification._id,
      requireInteraction: false,
      silent: false,
    };

    try {
      const notif = new Notification(
        notification.title || 'New Notification',
        options,
      );

      notif.onclick = () => {
        window.focus();
        notif.close();

        if (notification.metadata?.actionUrl) {
          window.location.href = notification.metadata.actionUrl;
        }
      };
    } catch (error) {}
  }

  setupConnectionHealthCheck() {
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }

    this.connectionCheckInterval = setInterval(() => {
      if (this.socket && !this.socket.connected && this.isConnected) {
        console.log(
          '⚠️ Socket appears disconnected, attempting manual reconnect...',
        );
        this.isConnected = false;

        setTimeout(() => {
          if (this.userId && this.token) {
            this.connect();
          }
        }, 5000);
      }

      if (this.socket && this.socket.connected) {
        if (!this.pingInterval) {
          this.pingInterval = setInterval(() => {
            if (this.socket && this.socket.connected) {
              this.socket.emit('ping', { timestamp: Date.now() });
            }
          }, 60000);
        }
      }
    }, 30000);
  }

  onNotification(callback) {
    if (typeof callback === 'function') {
      this.notificationCallbacks.push(callback);

      return () => {
        const index = this.notificationCallbacks.indexOf(callback);
        if (index > -1) {
          this.notificationCallbacks.splice(index, 1);
        }
      };
    }
    return () => {};
  }

  onSync(callback) {
    if (typeof callback === 'function') {
      this.syncCallbacks.push(callback);

      return () => {
        const index = this.syncCallbacks.indexOf(callback);
        if (index > -1) {
          this.syncCallbacks.splice(index, 1);
        }
      };
    }
    return () => {};
  }

  offNotification(callback) {
    const index = this.notificationCallbacks.indexOf(callback);
    if (index > -1) {
      this.notificationCallbacks.splice(index, 1);
    }
  }

  offSync(callback) {
    const index = this.syncCallbacks.indexOf(callback);
    if (index > -1) {
      this.syncCallbacks.splice(index, 1);
    }
  }

  markAsRead(notificationId) {
    if (this.socket && this.isConnected) {
      console.log(
        '📝 Marking notification as read via socket:',
        notificationId,
      );
      this.socket.emit('notification_read', {
        notificationId,
        userId: this.userId,
        timestamp: new Date().toISOString(),
      });
    } else {
      console.warn('⚠️ Cannot mark as read: Socket not connected');
    }
  }

  sendPing() {
    if (this.socket && this.isConnected) {
      this.socket.emit('ping', { timestamp: Date.now() });
    }
  }

  getStatus() {
    const socketConnected = this.socket ? this.socket.connected : false;

    return {
      isConnected: socketConnected && this.isConnected,
      socketId: this.socket?.id,
      userId: this.userId,
      baseUrl: this.baseUrl,
      reconnectionAttempts: this.reconnectionAttempts,
      transport: this.socket?.io?.engine?.transport?.name,
      notificationCallbacks: this.notificationCallbacks.length,
      syncCallbacks: this.syncCallbacks.length,
      lastConnection: this.lastConnectionTime,
    };
  }

  reconnect() {
    this.disconnect();
    setTimeout(() => {
      this.connect();
    }, 1000);
  }

  disconnect() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
      this.connectionCheckInterval = null;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.reconnectionAttempts = 0;
  }

  testConnection() {
    if (this.socket && this.isConnected) {
      this.socket.emit('test', {
        message: 'Test from frontend',
        timestamp: Date.now(),
      });
      return true;
    } else {
      return false;
    }
  }

  sendTestNotification() {
    if (this.socket && this.isConnected) {
      const testNotification = {
        _id: 'test-' + Date.now(),
        type: 'system_alert',
        title: 'Test Notification',
        message: 'This is a test notification sent from the frontend',
        read: false,
        createdAt: new Date().toISOString(),
        metadata: {
          test: true,
        },
      };

      this.socket.emit('test_notification', testNotification);

      return true;
    } else {
      console.warn('⚠️ Cannot send test notification: Socket not connected');
      return false;
    }
  }

  debugListeners() {
    if (this.socket) {
      const listeners = this.socket._callbacks || {};
      return Object.keys(listeners);
    }
    return [];
  }
}

const socketService = new SocketService();

export const initializeSocket = () => {
  const token =
    localStorage.getItem('token') || localStorage.getItem('jwtToken');
  const userData = localStorage.getItem('user');

  if (token && userData) {
    setTimeout(() => {
      socketService.connect();
    }, 1000);
  } else {
  }
};

if (process.env.NODE_ENV === 'development') {
  window.socketService = socketService;
}

export default socketService;
